/* eslint-disable react/jsx-props-no-spreading */
import { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import LinkField from "@shared/v2/LinkField";
// eslint-disable-next-line import/no-unresolved
import socialMediaNormalizer from "@shared/socialMediaNormalizer";
import * as creators from "../actions/creators";
import * as api from "../actions/api";
import Card from "./Card";
import DisplayRow from "./DisplayRow";

const URLLinkField = ({ value, onChange, onBlur, onKeyPress, ...rest }) => (
  <LinkField
    {...rest}
    placeholder="Enter username or link"
    containerClassName="tw-w-full tw-flex-1"
    autoFocus
    hideActions
    value={value}
    onBlur={onBlur}
    onKeyPress={onKeyPress}
    onChange={(e) => onChange(e.target.value)}
  />
);

URLLinkField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onKeyPress: PropTypes.func,
};

URLLinkField.defaultProps = {
  value: "",
  onBlur: () => {},
  onKeyPress: () => {},
};

const SECTIONS = [
  {
    title: "Facebook",
    key: "facebook",
    editable: true,
    domain: "facebook.com",
    comp: (rest) => URLLinkField({ ...rest }),
    errorMessages: {
      characterError: <span>Unable to save Facebook link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>facebook.com</b>
        </span>
      ),
    },
  },
  {
    title: "Instagram",
    key: "instagram",
    editable: true,
    domain: "instagram.com",
    comp: (rest) => URLLinkField({ ...rest }),
    errorMessages: {
      characterError: <span>Unable to save Instagram link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>instagram.com</b>
        </span>
      ),
    },
  },
  {
    title: "X",
    key: "twitter",
    editable: true,
    domain: "x.com",
    exceptions: ["twitter.com"],
    comp: (rest) => URLLinkField({ ...rest }),
    errorMessages: {
      characterError: <span>Unable to save X link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>x.com</b> or <b>twitter.com</b>
        </span>
      ),
    },
  },
  {
    title: "LinkedIn",
    key: "linkedin",
    editable: true,
    domain: "linkedin.com/in",
    exceptions: ["linkedin.com/company"],
    comp: (rest) => URLLinkField({ ...rest }),
    errorMessages: {
      characterError: <span>Unable to save LinkedIn link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>linkedin.com</b>
        </span>
      ),
    },
  },
  {
    title: "TikTok",
    key: "tiktok",
    editable: true,
    domain: "tiktok.com",
    comp: (rest) => URLLinkField({ ...rest }),
    errorMessages: {
      characterError: <span>Unable to save TikTok link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>tiktok.com</b>
        </span>
      ),
    },
  },
  {
    title: "YouTube",
    key: "youtube",
    editable: true,
    domain: "youtube.com",
    exceptions: ["youtube.com/channel", "youtube.com/c", "youtube.com/user"],
    comp: (rest) => URLLinkField({ ...rest }),
    errorMessages: {
      characterError: <span>Unable to save YouTube link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>youtube.com</b>
        </span>
      ),
    },
  },
  {
    title: "Pinterest",
    key: "pinterest",
    editable: true,
    domain: "pinterest.com",
    comp: (rest) => URLLinkField({ ...rest }),
    errorMessages: {
      characterError: <span>Unable to save Pinterest link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>pinterest.com</b>
        </span>
      ),
    },
  },
];

const SocialCard = ({ model, setValue, saveValue, setCustomToast }) => {
  const [errorStates, setErrorStates] = useState({
    facebook: null,
    twitter: null,
    linkedin: null,
    instagram: null,
    youtube: null,
    pinterest: null,
    tiktok: null,
  });

  const [preSaveValue, setPreSaveValue] = useState(null);

  return (
    <Card title="Social Media">
      {SECTIONS.map((sec) => (
        <DisplayRow
          key={sec.key}
          title={sec.title}
          editable={sec.editable}
          value={model[sec.key]}
          onChange={(val) => {
            if (!preSaveValue && sec.editable) {
              setPreSaveValue(model[sec.key]);
            }

            setValue(sec.key, val);
          }}
          errorHandler={(val) => {
            const { error } = socialMediaNormalizer({
              input: val,
              domain: sec.domain,
              exceptions: sec.exceptions || [],
              errorMessages: sec.errorMessages,
            });

            return error;
          }}
          onSave={(val) => {
            const { error, success } = socialMediaNormalizer({
              input: val,
              domain: sec.domain,
              exceptions: sec.exceptions || [],
              errorMessages: sec.errorMessages,
            });

            setPreSaveValue(null);

            if (error) {
              setValue(sec.key, preSaveValue);
              setErrorStates((prev) => ({ ...prev, [sec.key]: error }));
              setCustomToast({
                text: error,
                show: true,
              });
              return;
            }

            saveValue(sec.key, success);
            setValue(sec.key, success);
          }}
          Comp={(rest) => sec.comp({ ...rest, error: errorStates[sec.key] ? "text-error" : null })}
        />
      ))}
    </Card>
  );
};

SocialCard.propTypes = {
  model: PropTypes.shape({}).isRequired,
  setValue: PropTypes.func.isRequired,
  saveValue: PropTypes.func.isRequired,
  setCustomToast: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  model: state.businessSettings.model,
});

export default connect(mapStateToProps, { ...api, ...creators })(SocialCard);
